import { resolveNavItem } from '../footer/resolveNavItem';
import useCustomerConfig from 'src/hooks/useCustomerConfig';
import useAuthentication from 'src/hooks/useAuthentication';
import useTranslate from 'src/hooks/useTranslate';
import useRouteCheck from 'src/hooks/useRouteCheck';
import { useCustomEmployeeText } from 'src/utils/CustomExperienceText';
import { getNavIcon } from './navIconRegistry';
import { STATEMENT_OF_GOOD_HEALTH_GROUP_ID } from 'src/constants/navGroupIds';
function toNavBarLink(item) {
    return {
        label: item.label,
        href: item.url,
        linkType: item.externalLink ? 'external' : 'local',
        onClick: item.onClick,
    };
}
/**
 * Maps the nav-menu API's INavMenu[] onto plain data for honest-ui's Nav
 * (Nav.Item -> Nav.Group -> Nav.Link), mirroring footerColumns.ts's
 * buildFooterColumns. Reuses resolveNavItem so SSO/angular/proveRequired/
 * VIS-DHMO branching stays in exactly one place, shared with the footer.
 */
export function buildNavItems(navigationMenu = [], context) {
    return navigationMenu.map((navMenu) => {
        const label = context.translate(navMenu.navLabel);
        if (navMenu.navGroups && navMenu.navGroups.length > 0) {
            return {
                id: navMenu.id,
                label,
                groups: navMenu.navGroups.map((group) => ({
                    id: group.id,
                    heading: group.id === STATEMENT_OF_GOOD_HEALTH_GROUP_ID
                        ? label
                        : group.groupLabel ? context.translate(group.groupLabel) : undefined,
                    links: group.navLinks.map((link) => ({
                        ...toNavBarLink(resolveNavItem(link, context)),
                        icon: getNavIcon(link.iconName),
                    })),
                })),
            };
        }
        // A solo top-level navLink's own `label` is often omitted by the nav-menu API (the
        // outer navLabel is treated as authoritative for the whole entry) — fall back to it so
        // resolveNavItem's translate() and onNavigate() callback always have a real label to use.
        const link = navMenu.navLink
            ? toNavBarLink(resolveNavItem({ ...navMenu.navLink, label: navMenu.navLink.label ?? navMenu.navLabel }, context))
            : undefined;
        return {
            id: navMenu.id,
            label,
            href: link?.href,
            linkType: link?.linkType,
            onClick: link?.onClick,
            icon: getNavIcon(navMenu.navLink?.iconName),
        };
    });
}
export function useNavItems(onNavigate) {
    const { navigationMenu } = useCustomerConfig();
    const { user } = useAuthentication();
    const { t } = useTranslate();
    const { navigationCheck } = useRouteCheck();
    const applyCustomEmployeeText = useCustomEmployeeText();
    const context = {
        translate: (translatable) => applyCustomEmployeeText(t(translatable)),
        isIdentityVerified: user?.isIdentityVerified,
        navigationCheck,
        onNavigate,
    };
    return buildNavItems(navigationMenu, context);
}
